/**
 * A full-screen pass: a fragment shader drawn as one quad over a render target, each fragment
 * working out its own texel as Godot's compute passes do from the invocation ID.
 */
import * as THREE from 'three';
import { perObject } from '../utils/perObject.js';
/**
 * Maps the unit plane onto NDC, and hands the fragment shader the target's uv, 0 to 1, as `vUv`.
 * A shader that needs the NDC position repeats `position.xy * 2.0`.
 */
export const FULL_SCREEN_VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy + 0.5;
  gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
}
`;
const quadCamera = new THREE.Camera();
/**
 * The quad a renderer draws every pass with, one per renderer like each pass. Three leaves a
 * dispose listener that closes over the renderer on each geometry and material it draws, so one
 * quad or pass shared by every renderer would keep each renderer that drew it alive.
 */
const quadFor = perObject(() => {
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1));
    // The quad ignores every matrix, so its bounds say nothing about where it lands.
    quad.frustumCulled = false;
    return quad;
});
/**
 * A pass that overwrites its target with `fragmentShader`, reading `uniforms`.
 *
 * @canonical A full-screen pass
 */
export function fullScreenPass(name, fragmentShader, uniforms) {
    return new THREE.ShaderMaterial({
        name,
        uniforms,
        vertexShader: FULL_SCREEN_VERTEX,
        fragmentShader,
        depthTest: false,
        depthWrite: false,
        blending: THREE.NoBlending,
        toneMapped: false,
    });
}
/** A full-screen pass whose uniforms are `uniformNames`, each empty until a draw sets it. */
export function namedUniformPass(name, fragmentShader, uniformNames) {
    const uniforms = Object.fromEntries(uniformNames.map((uniform) => [uniform, { value: null }]));
    return fullScreenPass(name, fragmentShader, uniforms);
}
/** `pass`, made to declare its own outputs, one per attachment of a multiple-target draw. */
export function multipleTargetPass(pass) {
    pass.glslVersion = THREE.GLSL3;
    return pass;
}
/** Sets each uniform `values` names on `pass`, and throws for a name the pass does not declare. */
export function setUniforms(pass, values) {
    for (const [name, value] of Object.entries(values)) {
        const uniform = pass.uniforms[name];
        if (!uniform)
            throw new Error(`expected a uniform of the pass, got ${name}`);
        uniform.value = value;
    }
}
/** Draws `pass` into `target`, reading `values`, after which the pass holds none of the textures it read. */
export function drawPass(renderer, pass, values, target) {
    setUniforms(pass, values);
    drawFullScreen(renderer, pass, target);
    releaseTextures(pass);
}
/** Lets go of the textures `pass` reads, as a pass outlives the targets a draw set. */
export function releaseTextures(pass) {
    for (const uniform of Object.values(pass.uniforms)) {
        if (uniform.value instanceof THREE.Texture)
            uniform.value = null;
    }
}
/**
 * A pass whose `fragmentShader` writes every texel's depth through `gl_FragDepth`, which always
 * passes, so the target takes the pass's depth as it takes its colour.
 */
export function fullScreenDepthPass(name, fragmentShader, uniforms) {
    const pass = fullScreenPass(name, fragmentShader, uniforms);
    pass.depthTest = true;
    pass.depthFunc = THREE.AlwaysDepth;
    pass.depthWrite = true;
    return pass;
}
/**
 * Draws `pass` into `layer` (a cube face or a 3D slice) and `mip` of `target`. A null `target` is
 * the drawing buffer.
 *
 * @canonical A full-screen pass
 */
export function drawFullScreen(renderer, pass, target, layer = 0, mip = 0) {
    const quad = quadFor(renderer);
    quad.material = pass;
    renderer.setRenderTarget(target, layer, mip);
    renderer.render(quad, quadCamera);
}
//# sourceMappingURL=fullScreenPass.js.map